import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";

import { TagsColumn, TagsPageFilters } from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { Codecs, PageFilterAndColumns } from "org/pageState/types";
import { unwrapPageStateAs } from "org/pageState/utils";

import { decodeOrFilter, encodeOrFilter } from "./common";
import { createEncodeColumnFn } from "./controls/common";

// Keep it in sync with the definition in impero_shareable_links/src/api/page_state/tags.rs
const ALL_COLUMNS: TagsColumn[] = ["categoryName", "categoryCount"];
const encodeTagsColumn: (col: string) => TagsColumn =
    createEncodeColumnFn(ALL_COLUMNS);

// Filters
//
function encodeTagsPageFilters(
    filters: Array<SimpleOrFilter>
): TagsPageFilters {
    return {
        tagName: encodeOrFilter("tagName", filters),
        categoryName: encodeOrFilter("categoryName", filters),
        categoryCount: encodeOrFilter("categoryCount", filters),
    };
}

function decodeTagsPageFilters(
    filters: TagsPageFilters
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];
    if (filters.tagName) {
        output.push(decodeOrFilter("tagName", filters.tagName));
    }
    if (filters.categoryName) {
        output.push(decodeOrFilter("categoryName", filters.categoryName));
    }
    if (filters.categoryCount) {
        output.push(decodeOrFilter("categoryCount", filters.categoryCount));
    }
    return output;
}

// Page state
//
type TagsPageStateWithType = PageStateData<"Tags"> & { type: "Tags" };

function decodeTagsPageState(
    pageState: TagsPageStateWithType
): PageFilterAndColumns {
    const manualPageState = unwrapPageStateAs(pageState, "Tags");
    const filter: SimpleAndFilter = {
        filters: decodeTagsPageFilters(manualPageState.filters),
    };
    const visibleColumnIds = new Set(manualPageState.columns);
    return {
        pageType: pageState.type,
        filter,
        visibleColumnIds,
        version: pageState.version ?? 0,
    };
}

function encodeTagsPageState(
    filterAndColumns: PageFilterAndColumns
): TagsPageStateWithType {
    const { filter, visibleColumnIds } = filterAndColumns;

    let filters: Array<SimpleOrFilter> = [];
    if (filter) {
        filters = filter.filters;
    }

    return {
        type: "Tags",
        filters: encodeTagsPageFilters(filters),
        columns: Array.from(visibleColumnIds).map(encodeTagsColumn),
        version: filterAndColumns.version,
    };
}

const pageStateTagsCodecs: Codecs<PageFilterAndColumns, TagsPageStateWithType> =
    {
        encode: encodeTagsPageState,
        decode: decodeTagsPageState,
    };

export { pageStateTagsCodecs };
